import React, { useEffect, useState, useCallback } from 'react';
import { CheckCircle, Download, Home, ShoppingCart, ArrowRight, Loader2, FileText, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { pollPaymentStatus, getDownloadUrl, getUpsellOffer, createCheckoutSession } from '../services/paymentService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Success = () => {
  const [status, setStatus] = useState('checking'); // checking, paid, failed
  const [productInfo, setProductInfo] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [upsell, setUpsell] = useState(null);
  const [downsell, setDownsell] = useState(null);
  const [showDownsell, setShowDownsell] = useState(false);
  const [upsellPurchasing, setUpsellPurchasing] = useState(false);

  const poll = useCallback(async (sid, attempts = 0) => {
    const maxAttempts = 8;
    const interval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('failed');
      return;
    }

    try {
      const result = await pollPaymentStatus(sid);
      if (result.payment_status === 'paid') {
        setStatus('paid');
        setProductInfo({
          id: result.product_id,
          title: result.product_title,
          type: result.product_type,
        });
        // Fetch upsell offer
        if (result.product_type === 'guide') {
          try {
            const offers = await getUpsellOffer(result.product_id);
            setUpsell(offers.upsell);
            setDownsell(offers.downsell);
          } catch (e) {
            console.warn('[Success] upsell fetch failed:', e?.message || e);
          }
        }
        return;
      }
      if (result.payment_status === 'expired') {
        setStatus('failed');
        return;
      }
      setTimeout(() => poll(sid, attempts + 1), interval);
    } catch (err) {
      console.warn('[Success] payment poll attempt failed:', err?.message || err);
      setTimeout(() => poll(sid, attempts + 1), interval);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get('session_id');
    if (sid) {
      setSessionId(sid);
      poll(sid);
    } else {
      setStatus('failed');
    }
  }, [poll]);

  const handleDownload = () => {
    if (productInfo && sessionId) {
      window.open(getDownloadUrl(productInfo.id, sessionId), '_blank');
    }
  };

  const handleUpsellPurchase = async (product) => {
    setUpsellPurchasing(true);
    try {
      const session = await createCheckoutSession(product.id);
      window.location.href = session.url;
    } catch (err) {
      console.warn('[Success] upsell checkout failed:', err?.message || err);
      alert('Unable to process. Please try again.');
    } finally {
      setUpsellPurchasing(false);
    }
  };

  const handleDeclineUpsell = () => {
    setUpsell(null);
    setShowDownsell(true);
  };

  const handleDeclineDownsell = () => {
    setShowDownsell(false);
    setDownsell(null);
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-gray-50 to-white" data-testid="success-loading">
        <Loader2 className="w-12 h-12 text-orange-500 animate-spin mb-4" />
        <p className="text-lg text-gray-600">Verifying your payment...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <a href="/" className="flex items-center gap-2" data-testid="logo-link">
              <div className="w-8 h-8 bg-gradient-to-br from-red-600 to-orange-500 rounded-lg flex items-center justify-center shadow-md">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div className="text-lg font-bold">
                <span className="text-red-700">Paperwork</span>
                <span className="text-gray-800">Depot</span>
              </div>
            </a>
            <div className="hidden md:flex items-center gap-6">
              <a href="/browse" className="text-gray-700 hover:text-red-700 font-medium text-sm transition-colors">Browse Guides</a>
              <a href="/bundles" className="text-gray-700 hover:text-red-700 font-medium text-sm transition-colors">Bundles</a>
              <a href="/category/lawsuits" className="text-gray-700 hover:text-red-700 font-medium text-sm transition-colors flex items-center gap-1">
                Civil Lawsuits <span className="text-xs">🔥</span>
                <span className="bg-emerald-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">NEW</span>
              </a>
              <a href="/free-guide" className="text-gray-700 hover:text-red-700 font-medium text-sm transition-colors">Free Guide</a>
              <a href="/support" className="text-gray-700 hover:text-red-700 font-medium text-sm transition-colors">Support</a>
            </div>
          </div>
        </div>
      </header>

      <div className="flex items-center justify-center p-4 pt-12">
        <div className="max-w-2xl w-full space-y-6">
          {/* Main success card */}
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 text-center" data-testid="success-card">
            <div className={`w-20 h-20 ${status === 'paid' ? 'bg-green-100' : 'bg-yellow-100'} rounded-full flex items-center justify-center mx-auto mb-6`}>
              {status === 'paid' ? (
                <CheckCircle className="w-12 h-12 text-green-600" />
              ) : (
                <AlertCircle className="w-12 h-12 text-yellow-600" />
              )}
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-testid="success-title">
              {status === 'paid' ? 'Purchase Successful!' : 'Payment Issue'}
            </h1>

            {status === 'paid' && productInfo ? (
              <div className="space-y-4">
                <p className="text-lg text-gray-600">Your payment has been processed successfully.</p>
                <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 my-6">
                  <h2 className="font-bold text-gray-900 mb-2">Purchased:</h2>
                  <p className="text-lg text-gray-800" data-testid="purchased-title">{productInfo.title}</p>
                </div>

                <Button
                  size="lg"
                  className="bg-gradient-to-r from-orange-500 to-red-600 text-white text-lg px-8 py-6 hover:from-orange-600 hover:to-red-700 shadow-lg"
                  onClick={handleDownload}
                  data-testid="download-btn"
                >
                  <Download size={24} className="mr-2" />
                  {productInfo.type === 'guide' ? 'Download Your Guide' : 'Download Your Bundle (PDF)'}
                </Button>

                <p className="text-sm text-gray-500 mt-2">Your download will start immediately.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-lg text-gray-600">
                  We couldn't verify your payment. Please check your email for confirmation or contact support.
                </p>
              </div>
            )}

            <div className="mt-8 pt-8 border-t border-gray-200 flex flex-wrap gap-3 justify-center">
              <Button variant="outline" size="lg" onClick={() => window.location.href = '/shop'} data-testid="continue-shopping-btn">
                <ShoppingCart size={18} className="mr-2" />
                Continue Shopping
              </Button>
              <Button variant="outline" size="lg" onClick={() => window.location.href = '/'} data-testid="home-btn">
                <Home size={18} className="mr-2" />
                Back to Home
              </Button>
            </div>
          </div>

          {/* Upsell card */}
          {status === 'paid' && upsell && !showDownsell && (
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-orange-300 rounded-2xl shadow-lg p-8 text-center" data-testid="upsell-card">
              <div className="inline-block bg-orange-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-4 uppercase tracking-wide">
                Special Offer
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Upgrade to the Full Category Bundle!</h2>
              <p className="text-gray-600 mb-4">
                Get all <strong>{upsell.title}</strong> guides at a massive discount.
              </p>
              <div className="text-4xl font-bold text-orange-600 mb-2">${upsell.price}</div>
              <p className="text-sm text-gray-500 mb-6">Save over {Math.round((1 - upsell.price / (upsell.guide_ids?.length * 9.99 || 199.8)) * 100)}% vs buying individually</p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-8 py-5 hover:from-orange-600 hover:to-red-700 shadow-lg"
                  onClick={() => handleUpsellPurchase(upsell)}
                  disabled={upsellPurchasing}
                  data-testid="upsell-buy-btn"
                >
                  {upsellPurchasing ? 'Processing...' : (
                    <>
                      <ArrowRight size={20} className="mr-2" />
                      Yes, Upgrade Now!
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={handleDeclineUpsell}
                  data-testid="upsell-decline-btn"
                >
                  No thanks
                </Button>
              </div>
            </div>
          )}

          {/* Downsell card */}
          {status === 'paid' && showDownsell && downsell && (
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-2xl shadow-lg p-8 text-center" data-testid="downsell-card">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Wait — How About a Smaller Bundle?</h2>
              <p className="text-gray-600 mb-4">
                Get <strong>{downsell.title}</strong> — 5 related guides at a great price.
              </p>
              <div className="text-3xl font-bold text-blue-600 mb-6">${downsell.price}</div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-8 py-5"
                  onClick={() => handleUpsellPurchase(downsell)}
                  disabled={upsellPurchasing}
                  data-testid="downsell-buy-btn"
                >
                  {upsellPurchasing ? 'Processing...' : 'Get This Bundle'}
                </Button>
                <Button variant="outline" size="lg" onClick={handleDeclineDownsell} data-testid="downsell-decline-btn">
                  No thanks, I'm good
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Success;
